<template>
  <v-container mt-7>
    <v-form ref="form" lazy-validation>
      <v-row>
        <v-col class="selectorColumn">
          <v-select
            v-model="orgChoice"
            :items="organizations"
            :menu-props="{ maxHeight: '400' }"
            label="Organizations"
            clearable
            outlined
            dense
          >
            <template v-slot:selection="{ item, index }">
              <span>{{ item }}</span>
              <span v-if="index === 1" class="grey--text caption"
                >+{{ orgChoice.length - 1 }}</span
              >
            </template>
          </v-select>
        </v-col>
        <v-col class="selectorColumn">
          <v-select
            v-model="insChoice"
            :items="insurances"
            item-text="Insurance"
            return-object
            :menu-props="{ maxHeight: '400' }"
            label="Select Insurance"
            clearable
            outlined
            dense
            required
            :rules="[this.insChoice.length !== 0 || 'Insurance is required']"
            @click:clear="$nextTick(() => (insChoice = []))"
          ></v-select>
        </v-col>
        <v-col class="selectorColumn" v-if="!groupDisabled">
          <v-autocomplete
            v-model="groupChoice"
            :items="groups"
            item-text="GROUP_NAME"
            item-value="BILLING_PROVIDER_NPI"
            :menu-props="{ maxHeight: '400' }"
            label="Groups"
            multiple
            clearable
            outlined
            dense
            :rules="[
              (v) => {
                if (this.user.role === 'Group') {
                  return groupChoice.length > 0 || 'Please select your group';
                } else {
                  return true;
                }
              },
            ]"
            class="newSelect"
          >
            <template v-slot:selection="{ item, index }">
              <div class="selection-text" v-if="index === 0">{{ item.GROUP_NAME }}</div>
              <span v-if="index === 1" class="grey--text caption"
                >+{{ groupChoice.length - 1 }}</span
              >
            </template>
          </v-autocomplete>
        </v-col>
        <v-col class="selectorColumn">
          <v-autocomplete
            v-model="provChoice"
            :items="providers"
            item-value="PROVIDER_ID"
            item-text="PROVIDER_NAME"
            :menu-props="{ maxHeight: '400' }"
            label="Providers"
            multiple
            clearable
            outlined
            dense
            :rules="[
              (v) =>
                (this.user.role === 'Provider' && provChoice.length > 0) ||
                this.user.role !== 'Provider' ||
                'Please select your provider',
            ]"
            class="newSelect"
          >
            <template v-slot:selection="{ item, index }">
              <div class="selection-text" v-if="index === 0">
                {{ item.PROVIDER_NAME }}
              </div>
              <span v-if="index === 1" class="grey--text caption"
                >+{{ provChoice.length - 1 }}</span
              >
            </template>
          </v-autocomplete>
        </v-col>
        <v-col class="selectorColumn">
          <v-autocomplete
            v-model="locChoice"
            :items="locations"
            item-text="PROVIDER_LOCATION"
            item-value="PRACTICE_ID"
            return-object
            :menu-props="{ maxHeight: '400' }"
            label="Locations"
            :disabled="locDisabled"
            multiple
            clearable
            outlined
            dense
          >
            <template v-slot:selection="{ item, index }">
              <div class="selection-text" v-if="index === 0">
                {{ item.PROVIDER_LOCATION || 'No Location Provided' }}
              </div>
              <span v-if="index === 1" class="grey--text caption"
                >+{{ locChoice.length - 1 }}</span
              >
            </template>
          </v-autocomplete>
        </v-col>
        <v-col class="selectorColumn">
          <v-select
            v-model="reportChoice"
            :items="reports"
            item-text="Report Type"
            return-object
            :menu-props="{ maxHeight: '400' }"
            label="Select Report"
            clearable
            outlined
            dense
            required
            :rules="[this.reportChoice.length !== 0 || 'Report type is required']"
            @click:clear="$nextTick(() => (reportChoice = []))"
          ></v-select>
          <v-alert
            v-if="reportChoice === 'High Cost Prescribers'"
            class="text-subtitle-2"
            dense
            :colored-border="true"
            border="bottom"
            color="warning"
          >
            This report runs data points over millions of documents and will take some
            time to complete.
          </v-alert>
        </v-col>
        <v-col v-if="reportChoice === 'Conditions Search'" class="selectorColumn">
          <v-select
            v-model="codeTypeChoice"
            :items="condCodeTypes"
            item-text="Code Type"
            return-object
            dense
            no-filter
            clearable
            outlined
            required
            :menu-props="{ maxHeight: '400' }"
            label="Select a Code Type"
            :rules="[this.codeTypeChoice.length !== 0 || 'Code Type is required']"
            @click:clear="$nextTick(() => (codeTypeChoice = []))"
          ></v-select>
        </v-col>
        <v-col v-if="reportChoice === 'Diagnostic Conditions'" class="selectorColumn">
          <v-autocomplete
            v-model="conditionsCodeField"
            item-text="CODE"
            :items="codeList"
            no-filter
            clearable
            outlined
            required
            label="Enter Condition Code"
            :search-input.sync="conditionsCodeSync"
            :menu-props="{ maxHeight: '400' }"
            dense
            :disabled="codeDisabled"
            :hint="
              reportChoice === 'Diagnostic Conditions'
                ? 'Enter a condition to see possible matches'
                : 'Enter a code to see possible matches'
            "
            persistent-hint
            :rules="[conditionsCodeField != '' || 'Please select a Code']"
            @click:clear="$nextTick(() => (conditionsCodeSync = ''))"
            v-click-outside="codeSnackbar"
            return-object
          ></v-autocomplete>
        </v-col>
        <v-col v-if="reportChoice === 'Conditions Search'" class="selectorColumn">
          <v-autocomplete
            v-model="conditionsCodeField"
            item-text="CODE"
            :items="codeList"
            no-filter
            clearable
            outlined
            required
            label="Enter Condition Code"
            :search-input.sync="conditionsCodeSync"
            :menu-props="{ maxHeight: '400' }"
            dense
            :disabled="codeDisabled"
            :hint="
              reportChoice === 'Diagnostic Conditions'
                ? 'Enter a condition to see possible matches'
                : 'Enter a code to see possible matches'
            "
            persistent-hint
            :rules="[conditionsCodeField != '' || 'Please select a Code']"
            @click:clear="$nextTick(() => (conditionsCodeSync = ''))"
            v-click-outside="codeSnackbar"
            return-object
          ></v-autocomplete>
        </v-col>
        <v-col v-if="reportChoice === 'Chronic Conditions'" class="selectorColumn">
          <v-select
            v-model="codeTypeChoice"
            :items="chronicConditions"
            item-text="Chronic Condition Type"
            return-object
            :menu-props="{ maxHeight: '400' }"
            label="Select a Condition"
            clearable
            outlined
            dense
            required
            :rules="[this.codeTypeChoice.length !== 0 || 'Report type is required']"
            @click:clear="$nextTick(() => (codeTypeChoice = []))"
          ></v-select>
        </v-col>
        <v-col class="selectorColumn">
          <v-menu
            ref="menu"
            v-model="menu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="startDate"
                label="Start Date"
                show-current="2018-01"
                append-icon="mdi-calendar"
                outlined
                dense
                readonly
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="startDate"
              :max="maxEndDate"
              show-current="false"
              type="month"
              @input="menu = false"
              no-title
              scrollable
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col class="selectorColumn">
          <v-menu
            ref="menu2"
            v-model="menu2"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="endDate"
                label="End Date"
                append-icon="mdi-calendar"
                outlined
                dense
                readonly
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="endDate"
              :max="maxEndDate"
              show-current="false"
              type="month"
              @input="menu2 = false"
              no-title
              scrollable
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col class="selectorColumn">
          <v-select
            v-model="percentageChoice"
            :items="percentageFilter"
            return-object
            :menu-props="{ maxHeight: '400' }"
            label="Show Top %"
            outlined
            dense
            required
            :disabled="percentageDisabled"
            persistent-hint
            :hint="percentageDisabled === true ? 'Not applicable for this report' : ''"
          ></v-select>
        </v-col>
        <v-col class="selectorColumn" v-if="reportChoice === 'Frequent Flyer'">
          <v-text-field
            v-model="minVisits"
            label="Minimum Visits"
            outlined
            dense
            type="Number"
          ></v-text-field>
        </v-col>
        <v-col class="selectorColumn">
          <v-btn @click="validate" color="secondary" depressed block class="py-5">
            Run Report
            <v-icon small>mdi-play</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <v-row>
      <v-col v-if="load" cols="12" class="text-center">
        <v-progress-circular
          color="secondary"
          indeterminate
          :size="50"
          :width="3"
        ></v-progress-circular>
      </v-col>
      <v-col
        v-else-if="(insChoice.length === 0 || reportChoice.length === 0) && !showReport"
        cols="12"
      >
        <v-alert :value="true" type="info"> Please select all required fields </v-alert>
      </v-col>

      <v-col
        v-else-if="(insChoice.length > 0 || reportChoice.length > 0) && !showReport"
        cols="12"
      >
        <v-alert :value="true" type="info"> Press run report to continue </v-alert>
      </v-col>

      <v-col
        v-else-if="reportData.length === 0 && showReport && !changeDetected"
        cols="12"
      >
        <v-alert :value="true" type="info">
          There is no data available for this selection. Please change your filter
          selections and run report again
        </v-alert>
      </v-col>

      <v-col v-else-if="showReport && changeDetected" cols="12">
        <v-alert :value="true" type="warning">
          Change detected: Run the report to see updated data
        </v-alert>
      </v-col>
      <UtilizationReports
        v-show="reportData.length > 0 && showReport"
        :organizations="orgChoice"
        :groups="groupNum"
        :providers="provNums"
        :locations="locChoice"
        :insurance="selectedInsurance"
        :startDate="startDate"
        :endDate="endDate"
        :reportType="chosenReport"
        :reportData="reportData"
        :chartInfo="chartInfo"
        :calculatedClaimValues="calculatedClaimValues"
        :numOfClaims="numOfClaims"
        :totalClaimCost="totalClaimCost"
        :avgClaimCost="avgClaimCost"
        :medianClaimValue="medianClaimValue"
        :conditionsCode="currentCondCode"
        :conditionsType="codeTypeChoice"
        :specialtyData="specialtyData"
        :minVisits="minVisits"
        @reportLimit="spliceData"
      />
    </v-row>
    <div>
      <v-snackbar v-model="selectedCodeBar" color="error" :timeout="4000">
        <h3>Please choose a Code from the dropdown.</h3>
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="selectedCodeBar = false"> Close </v-btn>
        </template>
      </v-snackbar>
    </div>
  </v-container>
</template>

<script>
import axios from 'axios';
import { debounce } from 'lodash';
import UtilizationReports from '../components/utilizationComponents/UtilizationReports.vue';

export default {
  components: {
    UtilizationReports,
  },
  data() {
    return {
      startDate: new Date(new Date().getFullYear(), new Date().getMonth() - 3)
        .toISOString()
        .substr(0, 7),
      endDate: new Date(new Date().getFullYear(), new Date().getMonth() - 1)
        .toISOString()
        .substr(0, 7),
      maxEndDate: new Date(new Date().getFullYear(), new Date().getMonth() - 1)
        .toISOString()
        .substr(0, 7),
      menu: false,
      modal: false,
      menu2: false,
      tab: null,
      user: '',
      orgChoice: [],
      groupChoice: [],
      provChoice: [],
      locChoice: [],
      insChoice: [],
      reportChoice: [],
      organizations: [],
      providers: [],
      groups: [],
      groupObject: [],
      groupNum: [],
      insurances: [],
      locations: [],
      reportData: [],
      reportType: '',
      insurance: '',
      headers: [],
      percentageFilter: ['5%', '10%', '25%', '50%', '100%'],
      percentageChoice: '100%',
      showTotals: true,
      numOfClaims: 0,
      totalClaimCost: 0,
      avgClaimCost: 0,
      medianClaimValue: 0,
      calculatedClaimValues: 0,
      reportLimit: 1,
      chartInfo: [],
      specialty: false,
      specialtyData: [],
      reports: [
        'Admissions',
        'Brand Rx',
        'Chronic Conditions',
        'Claims Expense',
        'Diagnostic Conditions',
        'ER Visits',
        'Frequent Flyer',
        'High Cost Patients',
        'High Cost Prescribers',
        'High Cost Rx',
        'Home Health',
        'Observations',
        'Outpatient',
        // 'Patient MLR',
        'Readmissions',
        'Specialists',
        'Conditions Search',
      ],
      load: false,
      chosenReport: '',
      showReport: false,
      changeDetected: false,
      provNums: [],
      // Sync refreshes what code is currently chosen.
      conditionsCodeSync: '',
      codeList: [],
      // Represents the Code that is currently typed, but not saved in the text field.
      conditionsCodeField: '',
      condCodeTypes: ['Diagnostic', 'CPT', 'HCPCS'],
      chronicConditions: [
        'Hypertension',
        'CVD',
        'Diabetes',
        'CHF',
        'COPD',
        'Arthritis',
        'Asthma',
      ],
      codeTypeChoice: [],
      selectedCodeBar: false,
      // Makes it so neither insurance, nor selected code are refreshed in the reports
      // until run report is clicked again.
      currentCondCode: '',
      selectedInsurance: [],
      minVisits: 0,
    };
  },
  mounted() {
    this.getUser();
  },
  methods: {
    validate() {
      if (this.$refs.form.validate()) {
        this.getReport();
      }
    },
    spliceData(e) {
      this.reportLimit = e;
      this.getReport();
    },
    async checkPresets() {
      if (
        this.user.role === 'Clinical'
        || this.user.role === 'Provider'
        || this.user.role === 'Group'
        || this.user.role === 'Affiliation'
      ) {
        this.setPresets();
      } else if (this.user.role === 'Member') {
        this.$router.push('/member');
      } else if (this.user.role === 'Clinical') {
        this.$router.push('/membership');
      } else {
        this.getOrgs();
        this.getInsurances();
      }
    },
    async setPresets() {
      if (this.user.role === 'Group' || this.user.role === 'Affiliation') {
        this.organizations = [this.user.organization];
        this.insurances = this.user.insurance;
      } else {
        this.organizations.push(this.user.organization);
      }
      // this.groups = this.user.group;
      // this.insurances = this.user.insurance;

      if (this.user.role === 'Provider') {
        this.insChoice = this.user.insurance[0];
        this.orgChoice = this.user.organization;
        this.groupChoice = this.user.group;
        this.getProvider(this.user.npi);
      } else if (this.user.role === 'Group' || this.user.role === 'Affiliation') {
        this.orgChoice = this.user.organization;
        this.insChoice = this.user.insurance[0];
      }
    },
    async getUser() {
      await axios
        .get('/api/user')
        .then((response) => {
          this.user = response.data[0];
        })
        .catch(err => this.$router.push('/login'));
      this.checkPresets();
    },
    async getOrgs() {
      await axios.get('api/list/organization').then((response) => {
        this.organizations = response.data;
        this.orgChoice = this.organizations[0];
      });
    },
    async getLocations() {
      await axios.get(`api/selection/${this.provChoice}/locations`).then((response) => {
        this.locations = (response.data || []).map((loc) => ({
          ...loc,
          PROVIDER_LOCATION: loc.PROVIDER_LOCATION != null ? loc.PROVIDER_LOCATION : 'No Location Provided'
        }));
      });
    },
    async getProviders() {
      await axios.get(`api/selection/${this.groupChoice}/providers`).then((response) => {
        this.providers = response.data;
      });
    },
    async getProvider(npi) {
      await axios.get(`api/selection/single/${npi}/provider`).then((response) => {
        this.providers = response.data;
      });
    },
    async getInsurances() {
      await axios.get('api/list/insurances').then((response) => {
        this.insurances = response.data;
      });
    },
    async getGroups() {
      // Assigns entire user.group array if only one insurance, the else if is there in case there is more than one insurance and can only access certain groups within each insurance
      if (
        (this.user.role === 'Group' || this.user.role === 'Provider')
        && this.insurances.length === 1
      ) {
        await axios.get(`api/list/npi/${this.user.npi}/groups`).then((response) => {
          this.groups = response.data;
        });
      } else if (this.user.role === 'Affiliation') {
        await axios.get(`api/list/ipaId/${this.user.npi}/groups`).then((response) => {
          this.groups = response.data;
        });
      } else if (this.user.role === 'Group' && this.insurances.length > 1) {
        await axios.get(`api/list/${this.insChoice}/groups`).then((response) => {
          const userObjGroups = this.user.group;
          const allPossibleGroups = response.data;
          this.groups = [];
          allPossibleGroups.forEach((grp) => {
            if (userObjGroups.indexOf(grp) !== -1) {
              this.groups.push(grp);
            }
          });
        });
      } else if (this.insChoice.length > 1) {
        await axios.get(`api/list/${this.insChoice}/groups`).then((response) => {
          this.groups = response.data;
        });
      }
    },
    cancelSearch() {
      if (this.cancelSource) {
        this.cancelSource.cancel('Start new search, stop active search');
      }
    },
    async getReport() {
      // Chreck if the codeField is not equal to the codeSync, if this is the case we do not want to run the report, empty the codeField variable and give the validation error snackbar
      if (
        this.reportChoice === 'Conditions Search'
        && this.conditionsCodeField.CODE.toUpperCase()
          !== this.conditionsCodeSync.toUpperCase()
      ) {
        this.conditionsCodeField = '';
        this.selectedCodeBar = true;
      } else {
        this.selectedInsurance = this.insChoice;
        this.load = true;
        this.showReport = true;
        this.changeDetected = false;
        const locs = this.locChoice;
        const providers = this.provChoice;
        let locations = [];

        if (locs.length > 0) {
          locs.forEach((loca) => {
            if (locations.indexOf(loca.PRACTICE_IDs) === -1) {
              locations = locations.concat(loca.PRACTICE_IDs);
            }
          });
        } else {
          locations = [];
        }
        // Check for pending request and if so cancel old one for new incoming
        this.cancelSearch();
        // Set the CancelToken
        this.cancelSource = axios.CancelToken.source();

        // Set minVisits to 0 if the input is blank
        if (this.minVisits === '') {
          this.minVisits = 0;
        }

        this.reportData = [];
        this.showTotals = true;
        this.numOfClaims = 0;
        this.totalClaimCost = 0;
        this.avgClaimCost = 0;
        this.medianClaimValue = 0;

        const getAllData = async () => {
          // Specialty route so that we can have all data loaded on inital table render, hardcoing the specialty param
          const specialtyUrl = `api/utilization/${this.reportChoice}/orgs=${this.orgChoice}/groups=${this.groupChoice}/providers=${providers}/locs=${locations}/${this.insChoice}/${this.startDate}/${this.endDate}/${this.reportLimit}/true/${this.conditionsCodeSync}/${this.codeTypeChoice}`;
          // const specData = await axios.get(specialtyUrl);

          // Patient data route, hardcoing the specialty param so that we pull all data for both Patients and Specialty toggle on inital render
          const url = `api/utilization/${this.reportChoice}/orgs=${this.orgChoice}/groups=${this.groupChoice}/providers=${providers}/locs=${locations}/${this.insChoice}/${this.startDate}/${this.endDate}/${this.reportLimit}/false/code=${this.conditionsCodeSync}/codetype=${this.codeTypeChoice}`;
          const tableData = await axios.get(url);

          // if (
          //   this.reportChoice === 'Admissions' ||
          //   this.reportChoice === 'Brand Rx' ||
          //   this.reportChoice === 'ER Visits' ||
          //   this.reportChoice === 'Outpatient'
          // ) {
          //   const chartData = await axios.get(
          //     `api/utilizationCharts/${this.reportChoice}/orgs=${this.orgChoice}/groups=${
          //       this.groupChoice
          //     }/providers=${providers}/locs=${locations}/${this.insChoice}/${this.startDate}/${
          //       this.endDate
          //     }/${this.reportLimit}/${this.specialty}/${null}`
          //   );
          //   this.chartInfo = chartData.data;
          // }
          if (this.reportChoice === 'Chronic Conditions') {
            this.currentCondCode = this.codeTypeChoice;
          } else {
            this.currentCondCode = this.conditionsCodeField;
          }
          this.chosenReport = this.reportChoice;
          // this.specialtyData = specData.data.data;
          this.reportData = tableData.data.data !== undefined ? tableData.data.data : tableData.data;
          this.numOfClaims = tableData.data.totalNumOfClaims;
          this.totalClaimCost = tableData.data.totalClaimCost;
          this.avgClaimCost = tableData.data.avgClaimCost;
          this.medianClaimValue = tableData.data.medianClaimValue;
          this.showTotals = true;
          this.load = false;
          this.cancelSource = null;
        };
        getAllData();
      }
    },
    getCodes() {
      if (this.conditionsCodeSync && this.conditionsCodeSync.length > 0) {
        if (this.reportChoice === 'Diagnostic Conditions') {
          // will get all names containing the typed in character and return an array of objects containing the name and code

          axios
            .get(`api/selection/getdiagbyname/${this.conditionsCodeSync}`)
            .then((response) => {
              try {
                this.codeList = response.data.data;
              } catch (err) {
                this.codeList = [];
              }
            });
        } else {
          // will return an array of all codes that contain the character the user has begun typing in

          axios
            .get(
              `api/selection/getcode/${this.conditionsCodeSync}/${this.codeTypeChoice}`,
            )
            .then((response) => {
              try {
                this.codeList = response.data.data;
              } catch (err) {
                this.codeList = [];
              }
            });
        }
      } else {
        this.codeList = [];
      }
    },
    codeSnackbar() {
      if (this.conditionsCodeField.length === 0 && this.codeList.length > 0) {
        this.selectedCodeBar = true;
      }
    },
  },
  watch: {
    groupChoice: debounce(async function groupChoice(newValue) {
      this.provChoice = [];
      if (this.showReport) {
        this.changeDetected = true;
      }

      if (this.groupChoice) {
        this.getProviders();
      }

      //   // Populates the group selector for group level logins with one insurance and one group available
      //   if (this.user.role === 'Group' && this.insurances.length === 1 && this.groups.length === 1) {
      //     this.groupChoice = this.user.group;
      //   }

      //   if (newValue.length > 0) {
      //     this.providers = [];
      //     this.provChoice = [];
      //     this.locations = [];
      //     this.locChoice = [];
      //     this.groupNum = [];
      //     console.log(newValue);

      //     // Check for pending request and if so cancel old one for new incoming
      //     this.cancelSearch();
      //     // Set the CancelToken
      //     this.cancelSource = axios.CancelToken.source();

      //     await axios.get(`api/selection/${this.insChoice}/group/${newValue}`).then((response) => {
      //       // Set cancelsource back to null
      //       this.cancelSource = null;
      //       const { data } = response;
      //       this.providers = response.data;
      //       data.forEach((element) => {
      //         if (this.user.role === 'Provider') {
      //           if (this.user.provider.includes(element.Provide_Name)) {
      //             this.providers.push(element);
      //             this.provChoice.push(element);
      //           }
      //         }

      //         if (this.insChoice) {
      //           if (this.groupNum.indexOf(element.PROVIDER_ID) === -1) {
      //             this.groupNum.push(element.PROVIDER_ID);
      //           }
      //         }
      //       });
      //     });
      //   } else {
      //     this.providers = [];
      //     this.provChoice = [];
      //     this.locChoice = [];
      //     this.locations = [];
      //     this.groupNum = [];
      //   }
    }, 300),

    provChoice() {
      if (this.showReport) {
        this.changeDetected = true;
      }

      if (this.provChoice) {
        this.getLocations();
      }

      // if (this.provChoice !== []) {
      //   const prov = this.provChoice;
      //   const allProviders = this.providers;
      //   const providerNames = [];
      //   this.locations = [];
      //   this.locChoice = [];
      //   this.provNums = [];

      //   // Create an Array of selected provider names
      //   prov.forEach((provs) => {
      //     providerNames.push(provs.PROVIDER_NAME);
      //   });
      //   // Loop through all the providers objects and see if there is a match with any names in providerNames array
      //   allProviders.forEach((p) => {
      //     if (providerNames.indexOf(p.PROVIDER_NAME) !== -1) {
      //       // Check for null values for the provider's Center_Location, if null we are pushing the PCP_Number to the locations selector
      //       if (p.PROVIDER_ID === null) {
      //         this.locations.push(`${p.PROVIDER_ID} - No Location Provided`);
      //       } else {
      //         // Add the location and add PCP_IDs, there can be multiple for a single provider
      //         this.locations.push(p.PROVIDER_LOCATION);
      //       }
      //       this.provNums.push(p.PROVIDER_ID);
      //     }
      //   });
      // } else {
      //   this.locChoice = [];
      //   this.locations = [];
      // }
    },
    async insChoice() {
      this.groupChoice = [];
      this.provChoice = [];
      this.locChoice = [];
      if (this.showReport) {
        this.changeDetected = true;
      }

      if (
        this.user.role === 'Admin'
        || this.user.role === 'Superuser'
        || this.user.role === 'Group'
        || this.user.role === 'Affiliation'
      ) {
        if (this.insChoice !== []) {
          this.getGroups();
        }
      } else {
        this.insurances = this.user.insurance;

        if (this.user.role === 'Provider') {
          this.groupChoice = this.user.group;
        }
      }
    },
    startDate() {
      if (this.showReport) {
        this.changeDetected = true;
      }
    },
    endDate() {
      if (this.showReport) {
        this.changeDetected = true;
      }
    },
    locChoice() {
      if (this.showReport) {
        this.changeDetected = true;
      }
    },
    reportChoice() {
      if (this.reportChoice == 'Diagnostic Conditions') {
        if (this.percentageChoice === '100%') {
          this.percentageChoice = '50%';
        }
        this.percentageFilter = ['5%', '10%', '25%', '50%'];
      } else {
        this.percentageFilter = ['5%', '10%', '25%', '50%', '100%'];
      }
      if (this.showReport) {
        this.changeDetected = true;
      }
      this.codeTypeChoice = [];
      this.conditionsCodeSync = '';
      this.conditionsCodeField = '';
    },
    percentageChoice() {
      if (this.showReport) {
        this.changeDetected = true;
      }
      if (this.percentageChoice === '5%') {
        this.reportLimit = 0.05;
      }
      if (this.percentageChoice === '10%') {
        this.reportLimit = 0.1;
      }
      if (this.percentageChoice === '25%') {
        this.reportLimit = 0.25;
      }
      if (this.percentageChoice === '50%') {
        this.reportLimit = 0.5;
      }
      if (this.percentageChoice === '100%') {
        this.reportLimit = 1;
      }
    },
    conditionsCodeSync() {
      this.getCodes();
    },
    conditionsCodeField() {
      if (this.showReport && this.conditionsCodeField !== '') {
        this.changeDetected = true;
      }
    },
    codeTypeChoice() {
      if (this.showReport) {
        this.changeDetected = true;
      }
    },
  },
  computed: {
    orgDisabled() {
      // if (this.user.role === 'Provider' || this.user.role === 'Group') {
      //   return true;
      // }
      return false;
    },
    insDisabled() {
      // if (this.user.role === 'Provider' || this.insurances.length == 1) {
      //   return true;
      // }
      return false;
    },
    provDisabled() {
      // if (this.groupChoice.length === 0) {
      //   this.provChoice = [];
      //   return true;
      // }
      // if (this.user.role === 'Provider') {
      //   return true;
      // }
      return false;
    },
    locDisabled() {
      if (
        this.reportChoice === 'Brand Rx'
      ) {
        this.locChoice = [];
        return true;
      }
      return false;
    },
    groupDisabled() {
      if (this.orgChoice.length === 0 || this.insChoice.length === 0) {
        this.groupChoice = [];
        return true;
      }
      if (this.user.role === 'Provider') {
        return true;
      }
      if (this.user.role === 'Group' && this.groups.length === 1) {
        this.groupChoice = this.user.group[0];
        return false;
      }
      return false;
    },
    percentageDisabled() {
      if (
        this.reportChoice === 'Patient MLR'
        || this.reportChoice === 'Claims Expense'
        || this.reportChoice === 'Brand Rx'
      ) {
        return true;
      }
      return false;
    },
    codeDisabled() {
      if (
        this.codeTypeChoice.length === 0
        && this.reportChoice !== 'Diagnostic Conditions'
      ) {
        return true;
      }
      return false;
    },
  },
};
</script>
<style scoped>
.newSelect .v-select__selection {
  white-space: nowrap;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
}

.selection-text {
  margin-right: 5px;
}
@media only screen and (min-width: 1200px) {
  .selection-text {
    white-space: nowrap;
    overflow: hidden;
    width: 70%;
  }
}
</style>
